import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import Register from "./pages/Register";
import Login from "./pages/Login";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import AccountDeletion from "./pages/AccountDeletion";
import ForgotPassword from "./pages/ForgotPassword";
import FillInformation from "./pages/FillInformation";
import DispatcherProfile from "./pages/completeProfiles/dispatchers";
import DriverProfile from "./pages/completeProfiles/drivers";
import DispatchConsole from "./pages/DispatchConsole";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRevenuePage from "./pages/admin/AdminRevenuePage";
import AdminPlansPage from "./pages/admin/AdminPlansPage";
import AdminAnnouncementsPage from "./pages/admin/AdminAnnouncementsPage";
import AdminSystemPage from "./pages/admin/AdminSystemPage";
import AdminTenantInviteCodesPage from "./pages/admin/AdminTenantInviteCodesPage";
import MfaSetupPage from "./pages/admin/MfaSetupPage";
import ImpersonationBanner from "./components/admin/ImpersonationBanner";
import { ensureUserInfo } from "./utils/auth";
import { PermissionProvider } from "./contexts/PermissionContext";
import { usePermissions } from "./hooks/usePermissions";
import TermsGate from "./components/legal/TermsGate";
import ExtensionBridge from "./components/ExtensionBridge";
// PCCManagement removed — merged into PortManagement with category tabs (Migration 071)

// ==================== Lazy-loaded dispatch-console child routes ====================
// Defers module loading until the route is first visited, reducing initial bundle size.
// Named exports use .then() wrapper to satisfy React.lazy's {default} requirement.

// default exports
const DriverTasks = React.lazy(() => import("./pages/DriverTasks"));
const DriverManagement = React.lazy(() => import("./pages/DriverManagement"));
const DriverPaymentPage = React.lazy(() => import("./pages/DriverPaymentPage"));
const ClientPaymentPage = React.lazy(() => import("./pages/ClientPaymentPage"));
const SalaryModelPage = React.lazy(() => import("./pages/SalaryModelPage"));
const PricingModelPage = React.lazy(() => import("./pages/ClientPricingPage")); // per-client pricing master-detail (folds in old PricingModelPage)
const EmailCenterPage = React.lazy(() => import("./pages/EmailCenterPage"));
// 邮件的两扇独立窗口：整条会话打印、单条会话弹出（Gmail 右上角那两个图标）。
// 都在 dispatch-console 之外 —— 窗口里不该再有左边那条导航。
const EmailThreadPrintPage = React.lazy(() => import("./pages/EmailThreadPrintPage"));
const EmailThreadWindowPage = React.lazy(() => import("./pages/EmailThreadWindowPage"));
const ReminderPage = React.lazy(() => import("./pages/ReminderPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const StatementManagementPage = React.lazy(() => import("./pages/StatementManagementPage"));
const InvoiceCountingPage = React.lazy(() => import("./pages/InvoiceCountingPage"));
const OrderDispatchPage = React.lazy(() => import("./pages/OrderDispatchPage"));
const ExpenseCountingPage = React.lazy(() => import("./pages/ExpenseCountingPage"));
const FinancialsPage = React.lazy(() => import("./pages/FinancialsPage"));
const ExecutivePage = React.lazy(() => import("./pages/ExecutivePage"));
const MessagesPage = React.lazy(() => import("./pages/MessagesPage"));
const DataAuditPage = React.lazy(() => import("./pages/DataAuditPage"));
const ScenarioTestPage = React.lazy(() => import("./pages/ScenarioTestPage"));

// named exports → wrap with .then() to provide {default}
const DispatchTask = React.lazy(() => import("./pages/dispatch/DispatchTask").then(m => ({ default: m.DispatchTask })));
const TruckManagement = React.lazy(() => import("./pages/TruckManagement").then(m => ({ default: m.TruckManagement })));
const YardManagement = React.lazy(() => import("./pages/YardManagement").then(m => ({ default: m.YardManagement })));
const ChassisManagement = React.lazy(() => import("./pages/dispatch/ChassisManagement").then(m => ({ default: m.ChassisManagement })));
const ShipperManagement = React.lazy(() => import("./pages/ShipperManagement").then(m => ({ default: m.ShipperManagement })));
const PortManagement = React.lazy(() => import("./pages/PortManagement").then(m => ({ default: m.PortManagement })));
const WarehouseManagement = React.lazy(() => import("./pages/WarehouseManagement").then(m => ({ default: m.WarehouseManagement })));
const ClientManagement = React.lazy(() => import("./pages/ClientManagement").then(m => ({ default: m.ClientManagement })));
const ReportsDashboard = React.lazy(() => import("./pages/reports/ReportsDashboard"));

/** Route guard: redirects to /login if no token exists */
function RequireAuth({ children }: { children: React.ReactElement }) {
  const token = localStorage.getItem("token");
  // Allow through if OAuth code is present — token exchange will happen in the child component
  const hasOAuthCode = new URLSearchParams(window.location.search).has("code");
  if (!token && !hasOAuthCode) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/** Route guard: redirects to /login if not a platform_admin.
 *  Reads role from the signed JWT payload (not localStorage.user), so a user
 *  cannot bypass by tampering localStorage — tampering the JWT would break
 *  its HMAC signature and fail backend authenticateToken.
 *
 *  MFA enforcement: platform_admin tokens carry an `mfa_verified` claim. If
 *  false (admin has not yet completed MFA setup, or token was issued before
 *  TOTP verify), redirect to /admin/mfa-setup so they can complete it. The
 *  setup page itself is exempt from this redirect to avoid an infinite loop —
 *  the setup endpoints only require authenticateToken, not requirePlatformAdmin. */
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  try {
    const parts = token.split(".");
    if (parts.length !== 3) throw new Error("malformed token");
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload.role !== "platform_admin") {
      return <Navigate to="/dispatch-console" replace />;
    }
    if (payload.mfa_verified !== true && location.pathname !== "/admin/mfa-setup") {
      return <Navigate to="/admin/mfa-setup" replace />;
    }
  } catch {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/** Route guard: checks page-level permission via PermissionContext */
function RequirePermission({ pageKey, children }: { pageKey: string; children: React.ReactElement }) {
  const { loading, hasPageAccess } = usePermissions();

  if (loading) {
    return <div className="flex items-center justify-center h-full text-slate-400">Loading...</div>;
  }

  if (!hasPageAccess(pageKey)) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-500">
        <p className="text-lg font-medium">Access Denied</p>
        <p className="text-sm">You do not have permission to access this page.</p>
      </div>
    );
  }

  return children;
}

// Apply saved display settings on startup via CSS custom properties
function initDisplaySettings() {
  try {
    const saved = localStorage.getItem('displaySettings');
    if (!saved) return;
    const s = JSON.parse(saved);
    const root = document.documentElement;
    if (s.theme) root.setAttribute('data-theme', s.theme);
    const fontMap: Record<string, string> = { small: '14px', medium: '16px', large: '18px', 'extra-large': '20px' };
    if (s.fontSize) root.style.setProperty('--app-font-size', fontMap[s.fontSize] || '16px');
    root.style.setProperty('--app-spacing-scale', s.compactMode ? '0.75' : '1');
    root.style.setProperty('--app-transition-duration', s.showAnimations === false ? '0ms' : '200ms');
    if (s.language) root.lang = s.language;
  } catch { /* ignore */ }
}
initDisplaySettings();

function App() {
  // Ensure user info is in localStorage (covers OAuth login edge case)
  useEffect(() => {
    ensureUserInfo().catch(console.error)
  }, [])

  return (
    <PermissionProvider>
    <BrowserRouter>
      <ImpersonationBanner />
      <TermsGate />
      {/* Renders nothing — keeps the Chrome extension enrolled from any page. */}
      <ExtensionBridge />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy/extension" element={<Privacy />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />
        <Route path="/register" element={<Register />} />
        <Route path="/admid" element={<Navigate to="/admin" replace />} />
        {/* Legacy/shorthand pricing URL → the pricing page lives under dispatch-console */}
        <Route path="/pricing-model" element={<Navigate to="/dispatch-console/pricing-model" replace />} />
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />
          <Route path="revenue" element={<AdminRevenuePage />} />
          <Route path="plans" element={<AdminPlansPage />} />
          <Route path="invite-codes" element={<AdminTenantInviteCodesPage />} />
          <Route path="announcements" element={<AdminAnnouncementsPage />} />
          <Route path="system" element={<AdminSystemPage />} />
          <Route path="data-audit" element={<DataAuditPage />} />
          <Route path="scenario-tests" element={<ScenarioTestPage />} />
          <Route path="mfa-setup" element={<MfaSetupPage />} />
        </Route>
        {/* 邮件独立窗口。免不了要单独鉴权：新开的窗口不经过 dispatch-console 那层。 */}
        <Route path="/email/thread/:threadId" element={
          <RequireAuth><RequirePermission pageKey="email">
            <React.Suspense fallback={<div className="p-6 text-slate-400">Loading…</div>}>
              <EmailThreadWindowPage />
            </React.Suspense>
          </RequirePermission></RequireAuth>
        } />
        <Route path="/email/print/:threadId" element={
          <RequireAuth><RequirePermission pageKey="email">
            <React.Suspense fallback={<div className="p-6 text-slate-400">Loading…</div>}>
              <EmailThreadPrintPage />
            </React.Suspense>
          </RequirePermission></RequireAuth>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/fill-information" element={<FillInformation />} />
        <Route path="/complete-profile/dispatcher" element={<DispatcherProfile />} />
        <Route path="/complete-profile/driver" element={<DriverProfile />} />
        <Route path="/dispatch-console" element={<RequireAuth><DispatchConsole /></RequireAuth>}>
          <Route index element={<Navigate to="tasks" replace />} />
          <Route path="tasks" element={null} />
          <Route path="dispatch-tasks" element={<RequirePermission pageKey="dispatch-tasks"><DispatchTask /></RequirePermission>} />
          <Route path="dispatch-task" element={<Navigate to="/dispatch-console/dispatch-tasks" replace />} />
          <Route path="truck-management" element={<RequirePermission pageKey="truck-management"><TruckManagement /></RequirePermission>} />
          <Route path="yard-management" element={<RequirePermission pageKey="yard-management"><YardManagement /></RequirePermission>} />
          {/* pcc-management route removed — CBP managed under Port Management */}
          <Route path="chassis-management" element={<RequirePermission pageKey="chassis-management"><ChassisManagement /></RequirePermission>} />
          <Route path="order-dispatch" element={<RequirePermission pageKey="order-dispatch"><OrderDispatchPage /></RequirePermission>} />
          <Route path="driver-task" element={<RequirePermission pageKey="driver-management"><DriverTasks /></RequirePermission>} />
          <Route path="driver-management" element={<RequirePermission pageKey="driver-management"><DriverManagement /></RequirePermission>} />
          <Route path="driver-payment" element={<RequirePermission pageKey="driver-payment"><DriverPaymentPage /></RequirePermission>} />
          <Route path="client-payment" element={<RequirePermission pageKey="client-payment"><ClientPaymentPage /></RequirePermission>} />
          <Route path="salary-model" element={<RequirePermission pageKey="salary-model"><SalaryModelPage /></RequirePermission>} />
          <Route path="pricing-model" element={<RequirePermission pageKey="pricing-model"><PricingModelPage /></RequirePermission>} />
          <Route path="email" element={<RequirePermission pageKey="email"><EmailCenterPage /></RequirePermission>} />
          <Route path="reminder" element={<RequirePermission pageKey="reminder"><ReminderPage /></RequirePermission>} />
          <Route path="settings/*" element={<RequirePermission pageKey="settings"><SettingsPage /></RequirePermission>} />
          <Route path="counting/statement" element={<RequirePermission pageKey="statement"><StatementManagementPage /></RequirePermission>} />
          <Route path="counting/invoice" element={<RequirePermission pageKey="invoice"><InvoiceCountingPage /></RequirePermission>} />
          <Route path="counting/expense" element={<RequirePermission pageKey="expense"><ExpenseCountingPage /></RequirePermission>} />
          <Route path="client-management" element={<RequirePermission pageKey="client-management"><ClientManagement /></RequirePermission>} />
          <Route path="shipper" element={<RequirePermission pageKey="shipper"><ShipperManagement /></RequirePermission>} />
          <Route path="port" element={<RequirePermission pageKey="port"><PortManagement /></RequirePermission>} />
          <Route path="warehouse" element={<RequirePermission pageKey="warehouse"><WarehouseManagement /></RequirePermission>} />
          <Route path="financials" element={<RequirePermission pageKey="financials"><FinancialsPage /></RequirePermission>} />
          <Route path="executive" element={<RequirePermission pageKey="executive"><ExecutivePage /></RequirePermission>} />
          <Route path="reports" element={<RequirePermission pageKey="reports"><ReportsDashboard /></RequirePermission>} />
          <Route path="messages" element={<RequirePermission pageKey="messages"><MessagesPage /></RequirePermission>} />
          {/* The reported-message queue moved under Settings — it is compliance
              work, not dispatch work. Old links keep working. */}
          <Route path="messages/moderation" element={<Navigate to="/dispatch-console/settings/moderation" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </PermissionProvider>
  );
}

export default App;
